import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { ChevronLeft, ChevronRight, BookOpen, Sparkles, Wand2 } from 'lucide-react';
import AlbumMagiqueDialog from './AlbumMagiqueDialog';
import { YearbookPage, YearbookElement, PageBackground } from '@/types/yearbook';
import MagicPageDialog from './MagicPageDialog';

interface InspirationBook {
  name: string;
  preview: string;
  pages: string[];
}

const inspirationBooks: InspirationBook[] = [
  {
    name: 'Lycées',
    preview: '/images_editor/book_elipse2.png',
    pages: Array.from({ length: 34 }, (_, i) =>
      `https://1year1book-assets.s3.amazonaws.com/yearbooks/demo/89e495e7941cf9e40e6980d14a16bf023ccd4c91/pages/demo-lycee/${String(i + 1).padStart(3, '0')}.jpg`
    ),
  },
  {
    name: 'Etudes Sup.',
    preview: '/images_editor/book_elipse4.png',
    pages: Array.from({ length: 36 }, (_, i) =>
      `https://1year1book-assets.s3.amazonaws.com/yearbooks/demo/89e495e7941cf9e40e6980d14a16bf023ccd4c91/pages/demo-ge/${String(i + 1).padStart(3, '0')}.jpg`
    ),
  },
  {
    name: 'Collèges',
    preview: '/images_editor/book_elipse3.png',
    pages: Array.from({ length: 36 }, (_, i) =>
      `https://1year1book-assets.s3.amazonaws.com/yearbooks/demo/89e495e7941cf9e40e6980d14a16bf023ccd4c91/pages/demo-college-2019/${String(i + 1).padStart(3, '0')}.jpg`
    ),
  },
  {
    name: 'Primaires',
    preview: '/images_editor/book_elipse1.png',
    pages: Array.from({ length: 36 }, (_, i) =>
      `https://1year1book-assets.s3.amazonaws.com/yearbooks/demo/89e495e7941cf9e40e6980d14a16bf023ccd4c91/pages/primaire/${String(i + 1).padStart(3, '0')}.jpg`
    ),
  },
  {
    name: 'Classe',
    preview: '/images_editor/book_elipse5.png',
    pages: Array.from({ length: 28 }, (_, i) =>
      `https://zephir-prod.s3.eu-west-1.amazonaws.com/demo/89e495e7941cf9e40e6980d14a16bf023ccd4c91/1118/liseuse_final/${String(i + 1).padStart(3, '0')}.jpg`
    ),
  }
];

interface InspirationPanelProps {
  pages: YearbookPage[];
  onApplyMagicPage: (pageIndex: number, elements: YearbookElement[], background?: PageBackground, layoutTemplateId?: number) => void;
  onNavigateToPage: (pageIndex: number) => void;
  isWememberz?: boolean;
}

const InspirationPanel: React.FC<InspirationPanelProps> = ({ pages, onApplyMagicPage, onNavigateToPage, isWememberz = false }) => {
  const [selectedBook, setSelectedBook] = useState<InspirationBook | null>(null);
  const [magicOpen, setMagicOpen] = useState(false);
  const [albumMagiqueOpen, setAlbumMagiqueOpen] = useState(false);

  if (isWememberz) {
    return (
      <div className="flex flex-col h-full overflow-hidden items-center justify-center px-4 text-center">
        <div className="relative rounded-2xl border border-editor-sidebar-active/30 bg-gradient-to-br from-editor-sidebar-active/10 via-editor-sidebar-hover/40 to-editor-sidebar-active/5 p-5 overflow-hidden w-full">
          <div className="absolute -top-8 -right-8 w-24 h-24 rounded-full bg-editor-sidebar-active/10 blur-2xl pointer-events-none" />
          <div className="absolute -bottom-6 -left-6 w-20 h-20 rounded-full bg-editor-sidebar-active/10 blur-xl pointer-events-none" />
          <div className="relative flex flex-col items-center gap-3">
            <Wand2 size={28} className="text-editor-sidebar-active" />
            <h3 className="text-base font-semibold text-editor-sidebar-foreground">Album Magique</h3>
            <p className="text-xs text-muted-foreground">Votre album en 3 clics</p>
            <p className="text-[11px] text-muted-foreground leading-snug">
              Générez automatiquement votre album ! Les photos sont placées automatiquement et vous pouvez ensuite les modifier à votre guise.
            </p>
            <button
              onClick={() => setAlbumMagiqueOpen(true)}
              className="w-full flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-lg text-[12px] font-semibold bg-primary text-primary-foreground hover:opacity-90 transition-opacity shadow-sm mt-1"
            >
              <Sparkles size={14} />
              Créer mon album
            </button>
          </div>
        </div>

        <AlbumMagiqueDialog
          open={albumMagiqueOpen}
          onOpenChange={setAlbumMagiqueOpen}
          pages={pages}
          onApplyMagicPage={onApplyMagicPage}
          onNavigateToPage={onNavigateToPage}
        />
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-center gap-1.5 pb-2 shrink-0">
        <Sparkles size={14} className="text-editor-sidebar-active" />
        <h3 className="text-sm font-medium text-editor-sidebar-foreground">Inspiration</h3>
      </div>

      {/* Book grid */}
      <div className="flex-1 min-h-0 flex flex-col gap-1.5">
        <p className="text-[10px] text-muted-foreground text-center leading-tight shrink-0">
          Explorez nos yearbooks pour trouver l'inspiration.
        </p>

        <div className="flex-1 min-h-0 grid grid-cols-2 gap-1.5 auto-rows-fr content-start">
          {inspirationBooks.map((book) => (
            <button
              key={book.name}
              onClick={() => setSelectedBook(book)}
              className="group relative flex flex-col items-center justify-center rounded-lg border border-editor-sidebar-border/40 bg-editor-sidebar-hover/20 hover:bg-editor-sidebar-hover hover:border-editor-sidebar-active/30 transition-all duration-200 cursor-pointer overflow-hidden p-1.5 min-h-0"
            >
              <div className="flex-1 min-h-0 flex items-center justify-center w-full">
                <img
                  src={book.preview}
                  alt={book.name}
                  className="max-h-full max-w-full object-contain group-hover:scale-105 transition-transform duration-300"
                />
              </div>
              <span className="text-[10px] font-medium text-editor-sidebar-foreground group-hover:text-editor-sidebar-active transition-colors mt-0.5 leading-tight truncate w-full text-center shrink-0">
                {book.name}
              </span>
            </button>
          ))}
          {inspirationBooks.length % 2 !== 0 && <div />}
        </div>
      </div>

      {/* Magic Page CTA */}
      <div className="shrink-0 pt-2 mt-auto">
        <div className="relative rounded-xl border border-editor-sidebar-active/30 bg-gradient-to-br from-editor-sidebar-active/10 via-editor-sidebar-hover/40 to-editor-sidebar-active/5 p-3 overflow-hidden">
          <div className="absolute -top-6 -right-6 w-20 h-20 rounded-full bg-editor-sidebar-active/10 blur-2xl pointer-events-none" />
          <div className="absolute -bottom-4 -left-4 w-16 h-16 rounded-full bg-editor-sidebar-active/8 blur-xl pointer-events-none" />
          <div className="relative flex flex-col items-center gap-1.5">
            <div className="flex items-center gap-1.5">
              <Wand2 size={13} className="text-editor-sidebar-active" />
              <span className="text-xs font-semibold text-editor-sidebar-foreground">Page magique</span>
            </div>
            <p className="text-[10px] text-muted-foreground text-center leading-snug">
              Créez une page unique en un clic grâce à nos modèles et vos photos.
            </p>
            <button
              onClick={() => setMagicOpen(true)}
              className="w-full flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-[11px] font-semibold bg-primary text-primary-foreground hover:opacity-90 transition-opacity shadow-sm mt-0.5"
            >
              <Sparkles size={12} />
              Générer une page
            </button>
          </div>
        </div>
      </div>

      <Dialog open={!!selectedBook} onOpenChange={(open) => !open && setSelectedBook(null)}>
        <DialogContent className="sm:max-w-5xl max-h-[90vh] flex flex-col">
          <DialogHeader className="text-center items-center">
            <DialogTitle>{selectedBook?.name}</DialogTitle>
            <p className="text-xs text-muted-foreground">
              📖 Liseuse interactive – Utilisez les flèches ← → ou cliquez pour tourner les pages
            </p>
          </DialogHeader>
          {selectedBook && <InspirationReader key={selectedBook.name} book={selectedBook} />}
        </DialogContent>
      </Dialog>

      <MagicPageDialog
        open={magicOpen}
        onOpenChange={setMagicOpen}
        pages={pages}
        onApplyMagicPage={onApplyMagicPage}
        onNavigateToPage={onNavigateToPage}
      />
    </div>
  );
};

/* ─────────── Inspiration Reader with realistic page flip ─────────── */

const FLIP_DURATION = 600;

const InspirationReader: React.FC<{ book: InspirationBook }> = ({ book }) => {
  const [currentPage, setCurrentPage] = useState(0); // start on cover (single view)
  const [flip, setFlip] = useState<{
    direction: 'next' | 'prev';
    frontSrc: string;
    backSrc: string | null;
  } | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const lastPage = book.pages.length - 1;
  const isFirstPage = currentPage === 0;
  const isLastPage = currentPage >= lastPage;
  const isSingleView = isFirstPage || isLastPage;

  const canPrev = currentPage > 0;
  const canNext = currentPage < lastPage;

  const goNext = useCallback(() => {
    if (flip || !canNext) return;
    const frontSrc = book.pages[currentPage];
    // From cover (single page), jump to page index 2 to show spread 002-003
    const step = currentPage === 0 ? 2 : 2;
    const nextPage = Math.min(lastPage, currentPage + step);
    const backSrc = currentPage + 1 < book.pages.length ? book.pages[currentPage + 1] : null;
    setFlip({ direction: 'next', frontSrc, backSrc });
    setTimeout(() => {
      setCurrentPage(nextPage);
      setFlip(null);
    }, FLIP_DURATION);
  }, [flip, canNext, currentPage, book.pages, lastPage]);

  const goPrev = useCallback(() => {
    if (flip || !canPrev) return;
    const frontSrc = currentPage > 0 ? book.pages[currentPage - 1] : null;
    const backSrc = currentPage >= 2 ? book.pages[currentPage - 2] : null;
    // If we'd land on page 0, go to single view (page 0)
    const prevPage = Math.max(0, currentPage - 2);
    setFlip({ direction: 'prev', frontSrc: frontSrc!, backSrc });
    setTimeout(() => {
      setCurrentPage(prevPage);
      setFlip(null);
    }, FLIP_DURATION);
  }, [flip, canPrev, currentPage, book.pages]);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') goPrev();
      if (e.key === 'ArrowRight') goNext();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [goPrev, goNext]);

  // Pages to display (static, below the flip)
  const leftPageIdx = currentPage > 0 ? currentPage - 1 : null;
  const rightPageIdx = currentPage < book.pages.length ? currentPage : null;

  // During flip animation, we show the *destination* spread underneath
  let staticLeftIdx = leftPageIdx;
  let staticRightIdx = rightPageIdx;
  if (flip) {
    if (flip.direction === 'next') {
      // Underneath: next spread
      const nextRight = Math.min(book.pages.length - 1, currentPage + 2);
      staticLeftIdx = currentPage + 1 < book.pages.length ? currentPage + 1 : null;
      staticRightIdx = nextRight < book.pages.length ? nextRight : null;
    } else {
      // Underneath: previous spread
      const prevRight = Math.max(0, currentPage - 2);
      staticLeftIdx = prevRight > 0 ? prevRight - 1 : null;
      staticRightIdx = prevRight < book.pages.length ? prevRight : null;
    }
  }

  const imgClass = "w-full h-full object-fill";
  const pageClass = "relative overflow-hidden bg-white flex-1";

  // For last page: ensure it's actually the last index
  const isOnLastPage = currentPage >= lastPage;

  return (
    <div className="flex-1 flex flex-col items-center gap-4 min-h-0">
      <div
        ref={containerRef}
        className="flex-1 flex items-center justify-center w-full min-h-0 relative"
      >
        {isSingleView && !flip ? (
          /* ── Single page view (first or last) ── */
          <div
            className="flex max-h-[65vh] book-perspective shadow-lg cursor-pointer"
            style={{ aspectRatio: '3/4' }}
            onClick={() => {
              if (isFirstPage) goNext();
              else goPrev();
            }}
          >
            <div className={pageClass} style={{ height: '100%' }}>
              <img
                src={book.pages[currentPage]}
                alt={`Page ${currentPage + 1}`}
                className={imgClass}
              />
            </div>
          </div>
        ) : (
          /* ── Double page spread ── */
          <div className="flex max-h-[65vh] book-perspective shadow-lg" style={{ aspectRatio: '6/4' }}>
            {/* Left page */}
            <div
              className={`${pageClass} ${canPrev && !flip ? 'cursor-pointer' : ''}`}
              style={{ height: '100%' }}
              onClick={() => !flip && goPrev()}
            >
              {staticLeftIdx !== null && staticLeftIdx >= 0 ? (
                <img src={book.pages[staticLeftIdx]} alt={`Page ${staticLeftIdx + 1}`} className={imgClass} />
              ) : (
                <div className="w-full h-full bg-muted/20" />
              )}
            </div>

            {/* Right page */}
            <div
              className={`${pageClass} ${canNext && !flip ? 'cursor-pointer' : ''}`}
              style={{ height: '100%' }}
              onClick={() => !flip && goNext()}
            >
              {staticRightIdx !== null && staticRightIdx < book.pages.length ? (
                <img src={book.pages[staticRightIdx]} alt={`Page ${staticRightIdx + 1}`} className={imgClass} />
              ) : (
                <div className="w-full h-full bg-muted/20" />
              )}
            </div>

            {/* ── Flip animation overlay ── */}
            {flip && flip.direction === 'next' && (
              <div
                className="page-flip-right"
                style={{ width: '50%', height: '100%', left: '50%' }}
              >
                <div className="page-front">
                  <img src={flip.frontSrc} alt="Turning" className={imgClass} />
                  <div
                    className="absolute inset-0 pointer-events-none"
                    style={{
                      background: 'linear-gradient(to right, rgba(255,255,255,0) 60%, rgba(255,255,255,0.15) 85%, rgba(255,255,255,0.3) 100%)',
                    }}
                  />
                </div>
                <div className="page-back bg-white">
                  {flip.backSrc ? (
                    <img src={flip.backSrc} alt="Back" className={imgClass} />
                  ) : (
                    <div className="w-full h-full bg-white" />
                  )}
                  <div
                    className="absolute inset-0 pointer-events-none"
                    style={{
                      background: 'linear-gradient(to left, rgba(0,0,0,0) 80%, rgba(0,0,0,0.1) 100%)',
                    }}
                  />
                </div>
              </div>
            )}

            {flip && flip.direction === 'prev' && (
              <div
                className="page-flip-left"
                style={{ width: '50%', height: '100%', right: '50%' }}
              >
                <div className="page-front">
                  <img src={flip.frontSrc} alt="Turning" className={imgClass} />
                  <div
                    className="absolute inset-0 pointer-events-none"
                    style={{
                      background: 'linear-gradient(to left, rgba(255,255,255,0) 60%, rgba(255,255,255,0.15) 85%, rgba(255,255,255,0.3) 100%)',
                    }}
                  />
                </div>
                <div className="page-back bg-white">
                  {flip.backSrc ? (
                    <img src={flip.backSrc} alt="Back" className={imgClass} />
                  ) : (
                    <div className="w-full h-full bg-white" />
                  )}
                  <div
                    className="absolute inset-0 pointer-events-none"
                    style={{
                      background: 'linear-gradient(to right, rgba(0,0,0,0) 80%, rgba(0,0,0,0.1) 100%)',
                    }}
                  />
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Navigation */}
      <div className="flex items-center gap-4 pb-2">
        <button onClick={goPrev} disabled={!canPrev || !!flip} className="p-2 rounded-full hover:bg-muted disabled:opacity-30 disabled:cursor-not-allowed transition-colors">
          <ChevronLeft size={20} />
        </button>
        <span className="text-sm text-muted-foreground min-w-[80px] text-center">
          {isSingleView ? `${currentPage + 1}` : `${(staticLeftIdx ?? 0) + 1} – ${Math.min((staticRightIdx ?? 0) + 1, book.pages.length)}`} / {book.pages.length}
        </span>
        <button onClick={goNext} disabled={!canNext || !!flip} className="p-2 rounded-full hover:bg-muted disabled:opacity-30 disabled:cursor-not-allowed transition-colors">
          <ChevronRight size={20} />
        </button>
      </div>
    </div>
  );
};

export default InspirationPanel;
